import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, TextField, FormControlLabel, Checkbox, Typography, CircularProgress } from '@mui/material';
import AuthCard from './AuthCard';
import { authService } from '../../services/api';
import axios from 'axios';
import { homeAfterSignIn } from '../../utils/homeAfterSignIn';

const Activation = () => {
  const navigate = useNavigate();
  const { token } = useParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    acceptTerms: true,
    acceptPrivacy: true
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [clientSlug, setClientSlug] = useState(null);
  // Set once the link is checked, so the logo is looked up in the link's workspace (S-4, agreed 18 Oct).
  const [linkChecked, setLinkChecked] = useState(false);
  const mounted = useRef(true);

  // Fetch user data from token on component mount
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.post('/api/auth/validate-activation-token', { token });
        
        if (response.data.success) {
          setFormData(prev => ({
            ...prev,
            firstName: response.data.user.firstName,
            lastName: response.data.user.lastName,
            email: response.data.user.email
          }));
          setIsValidatingToken(false);
        } else {
          setMessage({
            type: 'error',
            text: response.data.message || 'Invalid or expired activation link'
          });
          setIsValidatingToken(false);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setMessage({
          type: 'error',
          text: 'Failed to validate activation link'
        });
        setIsValidatingToken(false);
      } finally {
        setLinkChecked(true);
      }
    };

    validateToken();

    return () => {
      mounted.current = false;
    };
  }, [token]);

  // Fetch logo
  // After the link's check, which gives the page the invitation's workspace, so the logo is that workspace's in any
  // browser (S-4, agreed 18 Oct). Removed 18 Oct 2026 (S-4): looking it up before, which gave the
  // browser's last workspace.
  useEffect(() => {
    if (!linkChecked) return;
    const fetchLogo = async () => {
      try {
        const res = await axios.get('/api/settings/logo'); 
        if (res.data.success && res.data.logoUrl) {
          setLogoUrl(res.data.logoUrl);
        }
        else
        {          
          setLogoUrl('/default-logo.png');
        }
        setClientSlug(res.data.slug || null);
      } catch (err) {
        console.error("Failed to fetch logo", err);
      }
    };
    
    fetchLogo();
  }, [linkChecked]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.acceptTerms || !formData.acceptPrivacy) {
      setMessage({
        type: 'error',
        text: 'Please accept both Terms of Service and Privacy Policy'
      });
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Password fields are required'
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage({
        type: 'error',
        text: 'Passwords do not match'
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.setNewPassword(
        token,
        formData.password,
        formData.confirmPassword
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: response.data.message || 'Account activated successfully'
        });

        // Auto-login
        try {
          const loginResponse = await authService.login({
            email: formData.email,
            password: formData.password
          });

          if (loginResponse.data.success) {
            localStorage.setItem('isAuthenticated', 'true');
            localStorage.setItem(
              'userData',
              JSON.stringify(loginResponse.data.user)
            );

            // Signed in after activation: the same landing as the sign-in page (K11 pt 5.1), so a super admin on the
            // admin site opens Workspaces. Removed 23 Oct 2026 (K11 pt 5.1, PR #8 review): always /dashboard.
            window.location.href = homeAfterSignIn(loginResponse.data.user);
            return;
          }
        } catch (loginError) {
          console.error('Auto-login error:', loginError);

          setTimeout(() => {
            if (mounted.current) {
              navigate('/login', {
                state: {
                  notification:
                    'Account activated successfully. Please login with your credentials.'
                }
              });
            }
          }, 3000);
        }
      } else {
        setMessage({
          type: 'error',
          text: response.data.message || 'Account activation failed'
        });
      }
    } catch (error) {
      console.error(error);

      setMessage({
        type: 'error',
        text:
          error.response?.data?.message ||
          'Something went wrong. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidatingToken) {
    return (
      <AuthCard logoUrl={logoUrl}>
        <div className="ac-spinner">
          <CircularProgress />
          <div className="ac-spinner-text">Validating activation link...</div>
        </div>
        <div className="ac-footer" />
      </AuthCard>
    );
  }

  return (
    <AuthCard logoUrl={logoUrl} badge="Internal account">
      <div className="ac-top">
        <div className="ac-title">Activate Your Account</div>
        <div className="ac-sub">Set your password to access training built for your team.</div>
      </div>

      <div className="ac-body">
        {message.text && (
          <div className={`ac-box ${message.type === 'success' ? 'ac-box-success' : 'ac-box-error'}`}>
            <div className="ac-box-title">{message.text}</div>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="ac-field">
            <div className="ac-field-label">First Name</div>
            <input
              type="text"
              value={formData.firstName}
              readOnly
              className="ac-field-input"
            />
          </div>

          <div className="ac-field">
            <div className="ac-field-label">Last Name</div>
            <input
              type="text"
              value={formData.lastName}
              readOnly
              className="ac-field-input"
            />
          </div>

          <div className="ac-field">
            <div className="ac-field-label">Password</div>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="ac-field-input"
              placeholder="Enter your password"
              required
            />
          </div>

          <div className="ac-field">
            <div className="ac-field-label">Confirm Password</div>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="ac-field-input"
              placeholder="Confirm your password"
              required
            />
          </div>

          <button type="submit" className="ac-btn-primary" disabled={isLoading}>
            {isLoading ? 'Activating...' : 'Activate and sign in'}
          </button>
        </form>
      </div>

      <div className="ac-footer" />
    </AuthCard>
  );
};

export default Activation;